import { Fragment, useState } from 'react';
import { Check, Eye, Plus, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { SelectNativo } from '@/components/ui/select-nativo';
import { Textarea } from '@/components/ui/textarea';
import { EstadoError, EstadoVacio } from '@/components/ui/estado-vacio';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { useCarga } from '@/features/dynamic-pricing/monitoreo/hooks/use-carga';
import { DialogoDePermisos } from '@/features/permissions/components/dialogo-de-permisos';
import { GRUPOS_DE_MODULOS, conImplicadas } from '@/features/permissions/estructura-de-permisos';
import { customRoleService } from '@/features/permissions/services/custom-role-service';
import { miEmpresaService } from '../services/mi-empresa-service';
import type { CustomRole } from '@/features/permissions/types/permission.types';
import {
  atributosSoporteNoPuede,
  AVISO_SOPORTE_NO_PUEDE,
  useEnSoporte,
} from '@/features/mini-prixia/soporte-solo-mira';
import { toast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils/cn';
import { MarcoDeMiEmpresa } from '../components/marco-de-mi-empresa';
import { colorDeCargo } from '../lib/cargos';

/**
 * Cada fila de la matriz: un permiso (o varios que van juntos), un ítem del
 * menú, o algo que decide el rol base y no se marca acá.
 */
type Fila =
  | { texto: string; permisos: string[] }
  | { texto: string; menu: string }
  | { texto: string; base: string[]; porque: string };

/** Las filas de la maqueta de Melanie, en su orden, con lo que de verdad gobiernan. */
const MATRIZ: Array<{ grupo: string; filas: Fila[] }> = [
  {
    grupo: 'Cotiza',
    filas: [
      { texto: 'Crear y editar cotizaciones', permisos: ['create_quotes', 'edit_own_quotes'] },
      { texto: 'Aprobar cotizaciones', permisos: ['approve_quotes', 'reject_quotes'] },
      { texto: 'Editar modelos de cálculo', permisos: ['manage_models'] },
    ],
  },
  {
    grupo: 'Compite',
    filas: [
      { texto: 'Ver monitoreo y analítica', menu: 'menu:pricing' },
      {
        texto: 'Decidir precios',
        base: ['COMPANY_ADMIN', 'APPROVER'],
        porque: 'Lo decide el rol base: pueden decidir los administradores y los aprobadores.',
      },
      {
        texto: 'Configurar reglas y estrategias',
        permisos: ['create_pricing_rules', 'edit_pricing_rules', 'toggle_pricing_rules'],
      },
    ],
  },
  {
    grupo: 'Mi empresa',
    filas: [
      { texto: 'Invitar y administrar usuarios', permisos: ['manage_users'] },
      { texto: 'Editar roles y permisos', permisos: ['manage_permissions'] },
    ],
  },
];

const ROLES_BASE = [
  { valor: 'COMPANY_ADMIN', texto: 'Administrador' },
  { valor: 'SALESPERSON', texto: 'Vendedor' },
  { valor: 'APPROVER', texto: 'Aprobador' },
];

/** El orden de las columnas: primero los que más pueden. */
const ORDEN_BASE: Record<string, number> = { COMPANY_ADMIN: 0, APPROVER: 1, SALESPERSON: 2 };

function tiene(rol: CustomRole, fila: Fila): boolean {
  if ('permisos' in fila) return fila.permisos.every((p) => rol.permissions.includes(p));
  if ('menu' in fila) return rol.menuItems.includes(fila.menu);
  return fila.base.includes(rol.baseRole);
}

/** El id de la columna del administrador: no es un rol personalizado, es el rol base. */
const ADMINISTRADOR = 'administrador-de-la-empresa';

/**
 * La columna «Administrador» de la maqueta. Quien administra la empresa no
 * tiene un rol personalizado: puede todo por su rol base, así que la columna
 * va marcada entera y no se edita.
 */
function rolDelAdministrador(personas: number): CustomRole {
  return {
    id: ADMINISTRADOR,
    name: 'Administrador',
    description: 'Quien administra la empresa: puede todo.',
    baseRole: 'COMPANY_ADMIN',
    isActive: true,
    permissions: MATRIZ.flatMap((g) => g.filas.flatMap((f) => ('permisos' in f ? f.permisos : []))),
    menuItems: GRUPOS_DE_MODULOS.flatMap((g) => g.items.map((i) => i.llave)),
    userCount: personas,
    createdAt: '',
    updatedAt: '',
  };
}

/** Mi empresa › Roles (maqueta de Melanie, 25-oct). */
export function RolesSection() {
  const { user, checkAuth } = useAuth();
  // Soporte sólo mira en Mi empresa: el servidor frena toda escritura de roles (SOPORTE_SOLO_MIRA).
  const enSoporte = useEnSoporte();
  const roles = useCarga(() => customRoleService.getAll(), []);
  const personas = useCarga(() => miEmpresaService.personas(), []);
  const [guardando, setGuardando] = useState<string | null>(null);
  const [nuevo, setNuevo] = useState(false);
  const [editando, setEditando] = useState<CustomRole | null>(null);
  const [viendoComo, setViendoComo] = useState<CustomRole | null>(null);
  const [duplicando, setDuplicando] = useState<CustomRole | null>(null);
  const [avanzado, setAvanzado] = useState<{
    rol: CustomRole;
    permisos: string[];
    menu: string[];
    bloqueados: string[];
  } | null>(null);

  const administradores = (personas.datos ?? []).filter(
    (p) => p.rol === 'COMPANY_ADMIN' && !p.customRoleId && p.estado !== 'INACTIVO'
  ).length;
  const columnas = [
    rolDelAdministrador(administradores),
    ...[...(roles.datos ?? [])]
      .filter((r) => r.isActive)
      .sort(
        (a, b) =>
          (ORDEN_BASE[a.baseRole] ?? 9) - (ORDEN_BASE[b.baseRole] ?? 9) ||
          a.name.localeCompare(b.name)
      ),
  ];

  const guardarPermisos = async (
    rol: CustomRole,
    permisos: string[],
    menu: string[],
    bloqueados: string[]
  ) => {
    await customRoleService.updatePermissions(rol.id, conImplicadas(permisos), menu, bloqueados);
    if (user?.customRoleId === rol.id) await checkAuth();
  };

  const alternar = async (rol: CustomRole, fila: Fila) => {
    if ('base' in fila || rol.id === ADMINISTRADOR) return;
    const clave = `${rol.id}:${fila.texto}`;
    setGuardando(clave);
    const marcado = tiene(rol, fila);
    try {
      if ('permisos' in fila) {
        const permisos = marcado
          ? rol.permissions.filter((p) => !fila.permisos.includes(p))
          : [...new Set([...rol.permissions, ...fila.permisos])];
        await guardarPermisos(rol, permisos, rol.menuItems, rol.lockedMenuItems ?? []);
      } else {
        const menu = marcado
          ? rol.menuItems.filter((m) => m !== fila.menu)
          : [...rol.menuItems, fila.menu];
        await guardarPermisos(rol, rol.permissions, menu, rol.lockedMenuItems ?? []);
      }
      roles.recargar();
    } catch (e: any) {
      toast({
        title: 'No se pudo cambiar el permiso',
        description: e?.response?.data?.error ?? e?.message,
        variant: 'destructive',
      });
    } finally {
      setGuardando(null);
    }
  };

  return (
    <MarcoDeMiEmpresa
      seccion="roles"
      acciones={
        <Button
          variant="degradado"
          size="lg"
          className="px-6"
          onClick={() => setNuevo(true)}
          {...atributosSoporteNoPuede(enSoporte)}
        >
          <Plus className="size-4" aria-hidden="true" />
          Nuevo rol
        </Button>
      }
    >
      {roles.estado === 'error' && !roles.datos ? (
        <EstadoError onReintentar={roles.recargar}>No pudimos cargar los roles.</EstadoError>
      ) : !roles.datos ? (
        <Skeleton className="h-96 w-full rounded-tarjeta" />
      ) : columnas.length === 1 ? (
        <EstadoVacio icono={ShieldCheck} titulo="Todavía no hay roles propios">
          Crea el primero con «Nuevo rol»: por ejemplo Gerente comercial o Analista de precios.
        </EstadoVacio>
      ) : (
        <section
          aria-label="Permisos por rol"
          className="overflow-x-auto rounded-tarjeta border bg-card shadow-card"
        >
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr>
                <th
                  scope="col"
                  className="w-[34%] px-6 pb-4 pt-6 text-left align-bottom text-[15px] font-medium text-muted-foreground"
                >
                  Permiso
                </th>
                {columnas.map((r) => (
                  <th
                    key={r.id}
                    scope="col"
                    className="px-3 pb-4 pt-6 text-center align-bottom font-normal"
                  >
                    <span className="flex flex-col items-center gap-1">
                      <button
                        type="button"
                        onClick={() => r.id !== ADMINISTRADOR && setEditando(r)}
                        title={
                          r.id === ADMINISTRADOR
                            ? 'Es el rol de quien administra la empresa: puede todo y no se edita'
                            : 'Editar el rol'
                        }
                        className={cn(
                          r.id === ADMINISTRADOR && 'cursor-default',
                          'rounded-full px-3 py-1 text-[15px] font-semibold transition-shadow hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          colorDeCargo(r.name)
                        )}
                      >
                        {r.name}
                      </button>
                      <span className="text-sm text-muted-foreground">
                        {r.userCount ?? 0} {(r.userCount ?? 0) === 1 ? 'persona' : 'personas'}
                      </span>
                      <button
                        type="button"
                        onClick={() => setViendoComo(r)}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                      >
                        <Eye className="size-3.5" aria-hidden="true" />
                        Ver como
                      </button>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MATRIZ.map((g) => (
                <Fragment key={g.grupo}>
                  <tr className="bg-muted/60">
                    <th
                      colSpan={columnas.length + 1}
                      scope="rowgroup"
                      className="px-6 py-2.5 text-left text-xs font-semibold tracking-[0.1em] text-muted-foreground"
                    >
                      {g.grupo.toUpperCase()}
                    </th>
                  </tr>
                  {g.filas.map((fila) => (
                    <tr key={fila.texto} className="border-b last:border-b-0">
                      <th scope="row" className="px-6 py-3.5 text-left text-[15px] font-normal">
                        {fila.texto}
                      </th>
                      {columnas.map((r) => {
                        const marcado = tiene(r, fila);
                        const clave = `${r.id}:${fila.texto}`;
                        const fijo = r.id === ADMINISTRADOR;
                        const soloLectura = 'base' in fila || fijo;
                        const porque = fijo
                          ? 'El administrador de la empresa puede todo.'
                          : 'base' in fila
                            ? fila.porque
                            : '';
                        const celda = (
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked={marcado}
                            aria-label={`${r.name}: ${fila.texto}`}
                            aria-disabled={soloLectura}
                            disabled={guardando === clave || enSoporte}
                            onClick={() => alternar(r, fila)}
                            className={cn(
                              'mx-auto grid size-8 place-items-center rounded-full border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                              marcado
                                ? 'border-ok bg-ok text-white'
                                : 'border-border bg-campo hover:border-primary/50', // pulido: permitido — tilde blanco sobre el verde de «tiene»
                              soloLectura && 'cursor-help',
                              guardando === clave && 'animate-pulse'
                            )}
                          >
                            {marcado && (
                              <Check className="size-4" strokeWidth={3} aria-hidden="true" />
                            )}
                          </button>
                        );
                        return (
                          <td key={r.id} className="px-3 py-2.5 text-center">
                            {soloLectura ? (
                              <Tooltip>
                                <TooltipTrigger asChild>{celda}</TooltipTrigger>
                                <TooltipContent className="max-w-xs">{porque}</TooltipContent>
                              </Tooltip>
                            ) : (
                              celda
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
          <p className="border-t px-6 py-3 text-sm text-muted-foreground">
            Toca un círculo para dar o quitar el permiso. Para el detalle completo (cada pantalla
            del menú), toca el nombre del rol.
          </p>
        </section>
      )}

      {nuevo && (
        <DialogoDeRol
          onCerrar={() => setNuevo(false)}
          onListo={() => {
            setNuevo(false);
            roles.recargar();
          }}
        />
      )}
      {editando && (
        <DialogoDeRol
          rol={editando}
          onCerrar={() => setEditando(null)}
          onListo={() => {
            setEditando(null);
            roles.recargar();
          }}
          onDuplicar={() => {
            setDuplicando(editando);
            setEditando(null);
          }}
          onPermisos={() => {
            setAvanzado({
              rol: editando,
              permisos: [...editando.permissions],
              menu: [...editando.menuItems],
              bloqueados: [...(editando.lockedMenuItems ?? [])],
            });
            setEditando(null);
          }}
        />
      )}
      {duplicando && (
        <DialogoDuplicarRol
          rol={duplicando}
          onCerrar={() => setDuplicando(null)}
          onListo={() => {
            setDuplicando(null);
            roles.recargar();
          }}
        />
      )}
      {viendoComo && <VerComo rol={viendoComo} onCerrar={() => setViendoComo(null)} />}
      {avanzado && (
        <DialogoDePermisos
          abierto
          onCerrar={() => setAvanzado(null)}
          nombreDelRol={avanzado.rol.name}
          funcionalidades={avanzado.permisos}
          onFuncionalidades={(permisos) => setAvanzado((a) => (a ? { ...a, permisos } : a))}
          visibles={avanzado.menu}
          onVisibles={(menu) => setAvanzado((a) => (a ? { ...a, menu } : a))}
          bloqueados={avanzado.bloqueados}
          onBloqueados={(bloqueados) => setAvanzado((a) => (a ? { ...a, bloqueados } : a))}
          guardando={guardando === 'avanzado'}
          onGuardar={async () => {
            setGuardando('avanzado');
            try {
              await guardarPermisos(
                avanzado.rol,
                avanzado.permisos,
                avanzado.menu,
                avanzado.bloqueados
              );
              toast({ title: 'Permisos actualizados', variant: 'success' });
              setAvanzado(null);
              roles.recargar();
            } catch (e: any) {
              toast({
                title: 'No se pudieron guardar',
                description: e?.message,
                variant: 'destructive',
              });
            } finally {
              setGuardando(null);
            }
          }}
        />
      )}
    </MarcoDeMiEmpresa>
  );
}

/** Crear un rol, o cambiarle el nombre, la descripción o el rol base. */
function DialogoDeRol({
  rol,
  onCerrar,
  onListo,
  onPermisos,
  onDuplicar,
}: {
  rol?: CustomRole;
  onCerrar: () => void;
  onListo: () => void;
  onPermisos?: () => void;
  onDuplicar?: () => void;
}) {
  const [nombre, setNombre] = useState(rol?.name ?? '');
  const [descripcion, setDescripcion] = useState(rol?.description ?? '');
  const [base, setBase] = useState(rol?.baseRole ?? 'SALESPERSON');
  const [guardando, setGuardando] = useState(false);
  const enSoporte = useEnSoporte();

  const guardar = async () => {
    setGuardando(true);
    try {
      if (rol) {
        await customRoleService.update(rol.id, {
          name: nombre.trim(),
          description: descripcion.trim() || null,
          baseRole: base,
        } as never);
      } else {
        await customRoleService.create({
          name: nombre.trim(),
          description: descripcion.trim() || undefined,
          baseRole: base,
        } as never);
      }
      toast({
        title: rol ? 'Rol actualizado' : `Rol «${nombre.trim()}» creado`,
        variant: 'success',
      });
      onListo();
    } catch (e: any) {
      toast({
        title: 'No se pudo guardar el rol',
        description: e?.response?.data?.error ?? e?.message,
        variant: 'destructive',
      });
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async () => {
    if (!rol) return;
    try {
      await customRoleService.delete(rol.id);
      toast({ title: `Rol «${rol.name}» borrado` });
      onListo();
    } catch (e: any) {
      toast({
        title: 'No se pudo borrar',
        description:
          e?.response?.data?.error ?? 'Tiene personas asignadas: cámbiales el rol primero.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open onOpenChange={(a) => !a && onCerrar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{rol ? `Editar «${rol.name}»` : 'Nuevo rol'}</DialogTitle>
          <DialogDescription>
            {rol
              ? 'El nombre se ve en Usuarios y en las reglas de aprobación.'
              : 'Nace con los permisos de su rol base; después los ajustas en la matriz.'}
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (nombre.trim()) void guardar();
          }}
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="rol-nombre">Nombre</Label>
            <Input
              id="rol-nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej.: Analista de precios"
              required
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="rol-descripcion">Descripción</Label>
            <Textarea
              id="rol-descripcion"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              rows={2}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="rol-base">Rol base</Label>
            <SelectNativo id="rol-base" value={base} onChange={(e) => setBase(e.target.value)}>
              {ROLES_BASE.map((r) => (
                <option key={r.valor} value={r.valor}>
                  {r.texto}
                </option>
              ))}
            </SelectNativo>
            <p className="text-xs text-muted-foreground">
              Decide lo que no se marca por permiso: quién decide precios y quién puede aprobar.
            </p>
          </div>
          <DialogFooter className="flex-wrap gap-2 sm:justify-between">
            {rol ? (
              <span className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onPermisos}
                  {...atributosSoporteNoPuede(enSoporte)}
                >
                  Permisos en detalle
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onDuplicar}
                  {...atributosSoporteNoPuede(enSoporte)}
                >
                  Duplicar
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive"
                  onClick={borrar}
                  {...atributosSoporteNoPuede(enSoporte)}
                >
                  Borrar
                </Button>
              </span>
            ) : (
              <span />
            )}
            <span className="flex gap-2">
              <Button type="button" variant="outline" onClick={onCerrar}>
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="degradado"
                disabled={!nombre.trim() || guardando || enSoporte}
                title={enSoporte ? AVISO_SOPORTE_NO_PUEDE : undefined}
                isLoading={guardando}
              >
                {rol ? 'Guardar' : 'Crear rol'}
              </Button>
            </span>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Duplicar un rol: nace con los mismos permisos y sin personas; sirve para partir un rol compartido por cargos distintos. */
function DialogoDuplicarRol({
  rol,
  onCerrar,
  onListo,
}: {
  rol: CustomRole;
  onCerrar: () => void;
  onListo: () => void;
}) {
  const [nombre, setNombre] = useState(`${rol.name} (copia)`);
  const [guardando, setGuardando] = useState(false);
  const enSoporte = useEnSoporte();

  const duplicar = async () => {
    setGuardando(true);
    try {
      await customRoleService.duplicate(rol.id, nombre.trim());
      toast({
        title: 'Rol duplicado',
        description: `Se creó «${nombre.trim()}» con los mismos permisos que «${rol.name}». Nace sin personas: asígnalo a quien corresponda y ajusta lo que distingue a cada cargo.`,
        variant: 'success',
      });
      onListo();
    } catch (e: any) {
      toast({
        title: 'No se pudo duplicar',
        description: e?.response?.data?.error ?? e?.message ?? 'No se pudo duplicar el rol',
        variant: 'destructive',
      });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(a) => !a && onCerrar()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Duplicar «{rol.name}»</DialogTitle>
          <DialogDescription>
            El rol nuevo parte con los mismos permisos. Después cambias sólo lo que lo distingue.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (nombre.trim()) void duplicar();
          }}
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="rol-duplicado-nombre">Nombre del rol nuevo</Label>
            <Input
              id="rol-duplicado-nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              required
            />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onCerrar}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="degradado"
              disabled={!nombre.trim() || guardando || enSoporte}
              title={enSoporte ? AVISO_SOPORTE_NO_PUEDE : undefined}
              isLoading={guardando}
            >
              Duplicar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** «Ver como»: lo que ve y lo que puede hacer alguien con ese rol. */
function VerComo({ rol, onCerrar }: { rol: CustomRole; onCerrar: () => void }) {
  const modulos = GRUPOS_DE_MODULOS.map((g) => ({
    titulo: g.titulo,
    items: g.items.filter((i) => rol.menuItems.includes(i.llave)).map((i) => i.nombre),
  })).filter((g) => g.items.length > 0);
  const puede = MATRIZ.flatMap((g) => g.filas.filter((f) => tiene(rol, f)).map((f) => f.texto));
  return (
    <Dialog open onOpenChange={(a) => !a && onCerrar()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Así ve Prixia un «{rol.name}»</DialogTitle>
          <DialogDescription>Lo que aparece en su menú y lo que puede hacer.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <p className="etiqueta-caps">En su menú</p>
            {modulos.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">Sólo Inicio y su perfil.</p>
            ) : (
              <ul className="mt-2 flex flex-col gap-2">
                {modulos.map((m) => (
                  <li key={m.titulo} className="text-sm">
                    <span className="font-semibold">{m.titulo}</span>
                    <span className="text-muted-foreground"> · {m.items.join(', ')}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="etiqueta-caps">Puede</p>
            <ul className="mt-2 flex flex-col gap-1.5">
              {puede.length === 0 ? (
                <li className="text-sm text-muted-foreground">Sólo mirar.</li>
              ) : (
                puede.map((p) => (
                  <li key={p} className="flex items-center gap-2 text-sm">
                    <Check className="size-4 text-ok" aria-hidden="true" />
                    {p}
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCerrar}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
